import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import transformSlots from '../transformers/transformSlots';
import transformVenues from '../transformers/transformVenues';

import {
  getCityCollections,
  getCollections,
  getCollectionsMarketing,
  getCollectionVenues,
  getCollectionVenuesForMarketing,
  getUserRecommendations,
  getVenuesBookTonight,
} from '../api';
import { ALL_DAY } from '../constants/resyCore';
import Logger from '../utils/logger';

const initialState = {
  lists: {
    marketing: {},
    all: {},
    leaderboards: {},
  },
  venues: {
    book_tonight: {},
    recommendations: {},
    marketing: {},
    other: {},
  },
};

const fetchLeaderboards = createAsyncThunk(
  'collections/getLeaderboardsStatus', getCityCollections
);

const fetchCollections = createAsyncThunk(
  'collections/getCollectionsStatus', getCollections
);

const fetchVenuesForCollection = createAsyncThunk(
  'collections/getVenuesForCollectionStatus', getCollectionVenues
);

const fetchVenueRecommendations = createAsyncThunk(
  'collections/getVenueRecommendationsStatus', getUserRecommendations
);

const fetchVenuesForBookTonight = createAsyncThunk(
  'collections/getVenuesForBookTonightStatus', getVenuesBookTonight
);

const fetchMarketingCollections = createAsyncThunk(
  'collections/getMarketingCollectionsStatus', getCollectionsMarketing
);

const fetchMarketingVenuesForCollection = createAsyncThunk(
  'collections/getMarketingVenuesForCollectionStatus', getCollectionVenuesForMarketing
);

const getFilteredLists = (list) => {
  if (!list) return null;
  const nonAlgorithmicTypeIds = [1, 2, 6, 8];
  const all = [...list].sort((a, b) => a.location_order_num - b.location_order_num);
  const algorithmic = all.filter((collection) => !nonAlgorithmicTypeIds.includes(
    collection.info.type_id
  ));
  const curated = all.filter((collection) => collection.info.type_id === 1);
  return { all, algorithmic, curated };
};

const handleError = (e = {}) => {
  const { message = '' } = e;
  Logger.error(`[ERROR] Collections Service: ${message}`);
};

// used for recommendations and book tonight
const getPersonalizedKey = (args) => {
  if (!args) return 'ny';
  const {
    location_id: location,
    day,
    party_size: party,
    isAuth,
    time_filter: time,
  } = args;

  const timeOfDay = time?.replaceAll(':', '').substr(0, 4) || ALL_DAY;
  return `${location}-${day}-${timeOfDay}-${party}-${isAuth}`;
};

const getVenueKey = (args) => {
  if (!args) return 'ny';
  const {
    location_id: location,
    collection_id: collection,
    day,
    party_size: party,
    isAuth,
    time_filter: time,
  } = args;

  const timeOfDay = time?.replaceAll(':', '').substr(0, 4) || ALL_DAY;
  if (!collection) return `${location}-${isAuth}`;
  return `${location}-${collection}-${day}-${timeOfDay}-${party}-${isAuth}`;
};

const getMarketingVenueKey = (args) => {
  if (!args) return 'ny';
  const {
    location_id: location,
    collection_id: collection,
    limit,
  } = args;

  if (!limit) return `${location}-${collection}`;
  return `${location}-${collection}-${limit}`;
};

const processVenueData = (data) => {
  if (!data) return null;
  return data.reduce(
    (results, venue) => {
      results.push(
        {
          ...venue,
          slots: transformSlots(venue.slots, { venue: venue.venue, gatingErrors: venue.gating_errors }),
          venue: transformVenues(venue),
        }
      );
      return results;
    }, []
  );
};

const collectionsSlice = createSlice({
  name: 'collections',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaderboards.pending, (state) => {
        state.lists.leaderboards = {
          isLoading: true,
        };
      })
      .addCase(fetchLeaderboards.fulfilled, (state, action) => {
        const {
          locationSlug,
        } = action?.meta?.arg;

        state.lists.leaderboards = {
          isLoading: false,
          [locationSlug]: [
            {
              info: {
                collectionSlug: 'climbing',
                image: 'https://s3.amazonaws.com/resy.com/images/lists/climbing.svg',
                name: 'Climbing',
                translationKey: 'climbing',
              },
              venues: action.payload[0].results.venues,
            },
            {
              info: {
                collectionSlug: 'top-rated',
                image: 'https://s3.amazonaws.com/resy.com/images/lists/toprated.svg',
                name: 'Top Rated',
                translationKey: 'top_rated',
              },
              venues: action.payload[1].results.venues,
            },
            {
              info: {
                collectionSlug: 'new-on-resy',
                image: 'https://s3.amazonaws.com/resy.com/images/lists/new.svg',
                name: 'New on Resy',
                translationKey: 'new_on_resy',
              },
              venues: action.payload[2].results.venues,
            },
          ],
        };
      })
      .addCase(fetchLeaderboards.rejected, (state, action) => {
        const {
          locationSlug,
        } = action?.meta?.arg;

        state.lists.leaderboards = {
          isLoading: false,
          [locationSlug]: null,
        };
        handleError(action.error);
      });

    builder
      .addCase(fetchCollections.pending, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
          isAuth,
        } = action?.meta?.arg;

        const key = `${location}-${algo}-${isAuth}`;
        state.lists.all = {
          isLoading: true,
          [key]: { isLoading: true },
        };
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
          isAuth,
        } = action?.meta?.arg;

        const key = `${location}-${algo}-${isAuth}`;
        const lists = getFilteredLists(action.payload);
        state.lists.all = {
          isLoading: false,
          [key]: lists,
        };
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
          isAuth,
        } = action?.meta?.arg;

        const key = `${location}-${algo}-${isAuth}`;
        state.lists.all[key] = null;
        handleError(action.error);
      });

    builder
      .addCase(fetchMarketingCollections.pending, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
        } = action?.meta?.arg;

        const key = `${location}-${algo}`;
        state.lists.marketing[key] = [];
      })
      .addCase(fetchMarketingCollections.fulfilled, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
        } = action?.meta?.arg;

        const key = `${location}-${algo}`;
        const lists = getFilteredLists(action.payload);
        state.lists.marketing[key] = lists;
      })
      .addCase(fetchMarketingCollections.rejected, (state, action) => {
        const {
          location_id: location,
          include_algo: algo,
        } = action?.meta?.arg;

        const key = `${location}-${algo}`;
        state.lists.marketing[key] = null;
        handleError(action.error);
      });

    builder
      .addCase(fetchVenuesForCollection.pending, (state, action) => {
        const key = getVenueKey(action.meta?.arg);
        state.venues.other.isLoading = true;
        state.venues.other[key] = null;
      })
      .addCase(fetchVenuesForCollection.fulfilled, (state, action) => {
        const key = getVenueKey(action.meta?.arg);
        const venueList = processVenueData(action.payload?.results?.venues, state);
        state.venues.other.isLoading = false;
        state.venues.other[key] = venueList;
      })
      .addCase(fetchVenuesForCollection.rejected, (state, action) => {
        const key = getVenueKey(action.meta?.arg);
        state.venues.other.isLoading = false;
        state.venues.other[key] = [];
        handleError(action.error);
      });

    builder
      .addCase(fetchMarketingVenuesForCollection.pending, (state, action) => {
        const key = getMarketingVenueKey(action.meta?.arg);
        state.venues.marketing.isLoading = true;
        state.venues.marketing[key] = [];
      })
      .addCase(fetchMarketingVenuesForCollection.fulfilled, (state, action) => {
        const key = getMarketingVenueKey(action.meta?.arg);
        const venueList = processVenueData(action.payload?.results?.venues, state);
        state.venues.marketing.isLoading = false;
        state.venues.marketing[key] = venueList;
      })
      .addCase(fetchMarketingVenuesForCollection.rejected, (state, action) => {
        const key = getMarketingVenueKey(action.meta?.arg);
        state.venues.marketing.isLoading = false;
        state.venues.marketing[key] = null;
        handleError(action.error);
      });

    builder
      .addCase(fetchVenueRecommendations.pending, (state) => {
        state.venues.recommendations.isLoading = true;
      })
      .addCase(fetchVenueRecommendations.fulfilled, (state, action) => {
        const params = {
          ...action.meta?.arg,
          location_id: action.meta?.arg?.location_id,
        };
        const key = getPersonalizedKey(params);
        const venueList = processVenueData(action.payload?.results, state);
        state.venues.recommendations.isLoading = false;
        state.venues.recommendations[key] = venueList;
      })
      .addCase(fetchVenueRecommendations.rejected, (state, action) => {
        const params = {
          ...action.meta?.arg,
          location_id: action.meta?.arg?.location_id,
        };
        const key = getPersonalizedKey(params);
        state.venues.recommendations.isLoading = false;
        state.venues.recommendations[key] = [];
        handleError(action.error);
      });

    builder
      .addCase(fetchVenuesForBookTonight.pending, (state) => {
        state.venues.book_tonight.isLoading = true;
      })
      .addCase(fetchVenuesForBookTonight.fulfilled, (state, action) => {
        const params = {
          ...action.meta?.arg,
          location_id: action.meta?.arg?.location,
        };
        const key = getPersonalizedKey(params);
        const venueList = processVenueData(action?.payload?.results?.venues, state);
        state.venues.book_tonight.isLoading = false;
        state.venues.book_tonight[key] = venueList;
      })
      .addCase(fetchVenuesForBookTonight.rejected, (state, action) => {
        const params = {
          ...action.meta?.arg,
          location_id: action.meta?.arg?.location,
        };
        const key = getPersonalizedKey(params);
        state.venues.book_tonight.isLoading = false;
        state.venues.book_tonight[key] = [];
        handleError(action.error);
      });
  },
});

export {
  fetchLeaderboards,
  fetchCollections,
  fetchVenuesForCollection,
  fetchVenuesForBookTonight,
  fetchVenueRecommendations,
  fetchMarketingCollections,
  fetchMarketingVenuesForCollection,
};

export default collectionsSlice.reducer;
